import { Link } from "react-router-dom";

const contact = "support@bookburrow.uk";

function Section({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section aria-labelledby={`terms-${number}`}>
      <h2 id={`terms-${number}`} className="font-display text-xl font-semibold text-forest-950">
        {number}. {title}
      </h2>
      <div className="mt-3 space-y-3">{children}</div>
    </section>
  );
}

export function Terms() {
  return (
    <div className="editorial-page legal-page">
      <header className="page-hero grain relative bg-cream-50 pb-8 pt-16 md:pt-20">
        <div className="relative z-10 mx-auto max-w-7xl px-6 text-center">
          <span className="text-sm font-semibold uppercase tracking-widest text-forest-500">Legal</span>
          <h1 className="mt-4 font-display text-4xl font-semibold tracking-tight text-forest-950 sm:text-5xl">
            Terms and conditions
          </h1>
          <p className="mx-auto mt-4 max-w-lg text-lg text-bark-600">Last updated 20 September 2026</p>
        </div>
      </header>

      <main className="bg-cream-50 py-16 md:py-24">
        <div className="mx-auto max-w-3xl px-6">
          <div className="prose prose-bark max-w-none space-y-10 leading-relaxed text-bark-700">
            <p>
              These terms cover the Bookburrow website, account portal, free trial, desktop
              software, licences, updates and support. Please read them before creating an
              account or placing an order.
            </p>

            <Section number={1} title="Who you are contracting with">
              <p>
                Bookburrow is supplied by Roguelite Software Ltd (&quot;we&quot;, &quot;us&quot;),
                company number 17052264, registered in England and Wales. Our registered office
                is Belmont Suite Paragon Business Park, Chorley New Road, Bolton, Lancashire,
                England, BL6 6HG. Contact us at <a href={`mailto:${contact}`}>{contact}</a>.
              </p>
              <p>
                The customer is the school, trust or other organisation named in the account
                or order. The person creating the account or buying a licence confirms that
                they may act for that customer. If you buy in your own name outside your trade
                or profession, you are a consumer and the consumer protections below apply.
              </p>
            </Section>

            <Section number={2} title="Accounts and the free trial">
              <p>
                Give accurate account and billing details, keep sign-in details secure, and
                tell us promptly about suspected unauthorised use. You are responsible for
                people you authorise to use your account or installation. Accounts are for
                staff and authorised helpers, not pupils.
              </p>
              <p>
                Eligible accounts receive a 30-day free trial without a payment card. When
                it ends, lending and returns are disabled until a licence is purchased.
                Your local catalogue and borrower data are not deleted. We may decline
                repeat trials intended to avoid buying a licence.
              </p>
            </Section>

            <Section number={3} title="Orders, prices and payment">
              <p>
                The product description, supported systems and current prices appear on our{" "}
                <Link to="/pricing">pricing page</Link> and at checkout. UK prices include
                applicable VAT. An agreed volume quote or invoice terms take precedence for
                that order. The price shown at checkout governs the transaction.
              </p>
              <p>
                Starting checkout places an order; we accept it when we confirm payment and
                make the licence or renewal available. If payment succeeds but we cannot
                fulfil the order, we will contact you and refund it. Stripe processes card
                payments. Keep your order confirmation and these terms for your records;
                contact us if you need a receipt or invoice.
              </p>
              <p>
                An initial licence is a one-off payment. Each optional support renewal is
                a separate one-off payment. We do not charge you automatically for another
                year. Later price changes do not affect a completed purchase.
              </p>
            </Section>

            <Section number={4} title="Your licence">
              <p>
                On acceptance of an initial order, we grant the customer a non-exclusive,
                perpetual right to install and use the supplied Bookburrow version on one
                computer at one school or library site. Staff and authorised helpers may use
                that installation for the customer&apos;s library operations. Another
                simultaneous computer or site needs a separate licence unless we agree
                otherwise in writing. Book and borrower counts do not affect the fee.
              </p>
              <p>
                You may move the installation to a replacement computer after deactivating
                the old one. If that is impossible, contact support for help. You may not
                sell, sublicense or transfer the licence to another organisation without
                written consent. Contact us about a school reorganisation or trust transfer.
              </p>
              <p>
                We and our licensors retain the software and its intellectual property.
                You may make copies reasonably needed for installation and backup. Do not
                redistribute the software, remove ownership notices or reverse engineer it,
                except where law permits. Your own library data remains yours.
              </p>
            </Section>

            <Section number={5} title="Updates and support">
              <p>
                An initial licence includes 12 months of available updates and support from
                purchase. An optional renewal adds 12 months to the later of the existing
                support end date or the renewal date. You can renew after a gap without
                paying for skipped years. Updates released during an active support period
                are included; future features are not guaranteed.
              </p>
              <p>
                If you do not renew, your licensed version continues to work without a time
                limit and your local data stays available. Later releases and ongoing
                support require renewal. We offer email support and, where available, live
                chat during UK business hours. We aim to respond within one working day,
                but cannot guarantee a resolution time. Support excludes on-site work,
                hardware and network administration.
              </p>
            </Section>

            <Section number={6} title="Cancellations and refunds">
              <p>
                We offer a 30-day money-back guarantee on each initial licence or renewal
                payment. Email <a href={`mailto:${contact}`}>{contact}</a> within 30 days
                of payment for a full refund, without giving a reason. We normally refund
                the original payment method. Refunding an initial licence ends the right
                to use that paid licence; refunding a renewal removes the support period
                it added. A refund does not delete locally stored data.
              </p>
              <p>
                Consumers may also have a statutory 14-day right to cancel a distance
                purchase. We do not treat a download or activation as a waiver of that
                right unless we first obtain the express consent and acknowledgement
                required for early supply of digital content. Our 30-day guarantee still
                applies if a statutory cancellation right has ended.
              </p>
              <p>
                Consumers can cancel by email or use the optional model form below. We
                will refund any amount due under law without undue delay and within 14
                days after being informed of the cancellation. Faulty or misdescribed
                software may give you further statutory remedies.
              </p>
              <div className="paper-card rounded-xl border border-cream-300 bg-white p-5 text-sm">
                <h3 className="font-semibold text-forest-950">Model cancellation form</h3>
                <p className="mt-2">
                  To: Roguelite Software Ltd, Belmont Suite Paragon Business Park,
                  Chorley New Road, Bolton, Lancashire, England, BL6 6HG, or{" "}
                  <a href={`mailto:${contact}`}>{contact}</a>
                  <br />I give notice that I cancel my contract for Bookburrow.
                  <br />Ordered on: [date]
                  <br />Customer name: [name]
                  <br />Customer address: [address]
                  <br />Date: [date]
                </p>
              </div>
            </Section>

            <Section number={7} title="Your data and backups">
              <p>
                You own the catalogue, borrower records and other content entered into
                Bookburrow. They are stored on your computer. You decide what personal
                data to enter, who may use the computer, and where backups are stored.
                You are responsible for a lawful basis, access controls, retention and
                backups, especially for pupil information.
              </p>
              <p>
                Configure the backup tools, keep a separate copy and periodically check
                that a backup can be restored. We do not ordinarily receive or host borrower
                records. Before sending records to support, remove pupil details where
                possible and agree a secure transfer with us. Our{" "}
                <Link to="/privacy">Privacy Policy</Link> explains how we handle account,
                payment, licence and support data.
              </p>
            </Section>

            <Section number={8} title="Acceptable use and security">
              <p>
                Use Bookburrow lawfully. Do not share keys or credentials outside your
                organisation, bypass activation limits, attack our services, introduce
                malicious code, or interfere with other customers. Report suspected
                security problems to <a href={`mailto:${contact}`}>{contact}</a>.
              </p>
            </Section>

            <Section number={9} title="Availability and product changes">
              <p>
                The activated desktop application is designed for offline day-to-day use.
                Internet access is needed for account services, activation, downloads,
                updates and third-party book lookups. Online services may be interrupted
                for maintenance, security or events outside our reasonable control. We
                will try to restore them promptly and communicate material disruptions.
              </p>
              <p>
                Future versions may change to address security, compatibility or legal
                needs. We will not deliberately remove core lending and catalogue
                functions from a version you have already licensed. If we discontinue
                updates or online support, we will give at least 90 days&apos; notice
                where reasonably practicable and explain the effect on existing licences.
              </p>
            </Section>

            <Section number={10} title="Suspension and termination">
              <p>
                We may suspend an account, online service or activation if we reasonably
                suspect fraud, a chargeback, a security risk or a serious breach. Where
                appropriate, we will explain why and allow a reasonable chance to resolve
                it. We will limit suspension to what is necessary and restore access when
                resolved. We may end an affected licence for an unresolved material breach
                after notice, or immediately for serious unlawful misuse. We will not
                revoke a paid licence just because optional support expires.
              </p>
              <p>
                You may stop using Bookburrow at any time. Closing an account does not
                itself create a refund right beyond section 6. Retain or export local data
                before removing the application. Provisions on ownership, liability and
                disputes continue where their nature requires it.
              </p>
            </Section>

            <Section number={11} title="Our responsibility">
              <p>
                We will supply Bookburrow with reasonable care and skill and as described
                at purchase. We do not promise that it will have no defects or work with
                every computer or third-party service. Tell us about a problem so we can
                investigate and provide a fix, replacement, refund or other appropriate
                remedy.
              </p>
              <p>
                Nothing limits liability for death or personal injury caused by
                negligence, fraud or fraudulent misrepresentation, or any liability
                that law does not allow us to limit. Nothing removes a consumer&apos;s
                statutory rights, including rights relating to digital content.
              </p>
              <p>
                For business and organisational customers, we are not responsible for
                unforeseeable losses or loss caused by a failure to take reasonable steps
                to protect or back up your own data. Subject to the paragraph above, our
                total liability arising from a paid licence or renewal is limited to the
                greater of £1,000 and twice the amount paid for that licence and its
                renewals, only to the extent that the limit is reasonable and lawful.
                We do not apply that cap to consumers.
              </p>
            </Section>

            <Section number={12} title="Changes to these terms">
              <p>
                We may update these terms for changes in law or how Bookburrow is supplied.
                The version accepted for a paid order governs that order. We will give
                reasonable advance notice of material changes to ongoing account or
                support services. If a change materially disadvantages you, contact us
                before it takes effect to discuss your options and any legal remedy.
                Posting new terms does not alter perpetual rights already granted.
              </p>
            </Section>

            <Section number={13} title="Law and disputes">
              <p>
                The law of England and Wales governs these terms. For organisational
                customers, courts in England and Wales have exclusive jurisdiction.
                Consumers may bring claims in courts available under applicable law.
                Please contact <a href={`mailto:${contact}`}>{contact}</a> first so we
                can try to resolve a dispute directly.
              </p>
              <p>
                If one provision is unenforceable, the others continue. Delay in
                enforcing a right does not waive it. No third party may enforce these
                terms.
              </p>
            </Section>
          </div>
        </div>
      </main>
    </div>
  );
}
